import type { Granularity } from "./ExpenseAnalyticsCharts.js";

/**
 * The Expense report's period presets, shared by the Report tab and the dashboard's
 * Expense report widget so both scope "this month" to the same days and draw it at
 * the same bucket grain.
 */

/** Local calendar date as YYYY-MM-DD (not UTC, so "today" is the viewer's today). */
export function isoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function isoToday(): string {
  return isoLocal(new Date());
}

// A view mode: the current-period presets. Note `Granularity` (day/week/month/year)
// is the API's bucket grain; these are periods — quarter/half map onto a month
// grain, not a new API grain.
export type Preset = "day" | "week" | "month" | "quarter" | "half" | "year";

export const PRESET_OPTIONS: { value: Preset; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "half", label: "Half Year" },
  { value: "year", label: "Year" },
];

// Each preset scopes the dashboard to the CURRENT period, to date, and picks the
// bucket grain used to draw the trend *within* that period:
//  Day     → today (by day)
//  Week    → from this week's Monday (by day)
//  Month   → from the 1st of this month (by day)
//  Quarter → from the 1st of this calendar quarter (by month)
//  Half    → from the 1st of this half-year, Jan or Jul (by month)
//  Year    → from 1 January of this year (by month)
// All end today, so the KPI cards and charts reflect the current period.
export function rangeAndGrain(mode: Preset): { from: string; to: string; granularity: Granularity } {
  const now = new Date();
  const to = isoLocal(now);
  const y = now.getFullYear();
  const mo = now.getMonth();
  switch (mode) {
    case "day":
      return { from: to, to, granularity: "day" };
    case "week": {
      const monday = new Date(now);
      const dow = (now.getDay() + 6) % 7; // Mon=0 … Sun=6
      monday.setDate(now.getDate() - dow);
      return { from: isoLocal(monday), to, granularity: "day" };
    }
    case "month":
      return { from: isoLocal(new Date(y, mo, 1)), to, granularity: "day" };
    case "quarter":
      return { from: isoLocal(new Date(y, Math.floor(mo / 3) * 3, 1)), to, granularity: "month" };
    case "half":
      return { from: isoLocal(new Date(y, mo < 6 ? 0 : 6, 1)), to, granularity: "month" };
    case "year":
      return { from: isoLocal(new Date(y, 0, 1)), to, granularity: "month" };
  }
}
